import { SOUND_PATHS } from "./audioSources.mjs";

/**
 * AudioManager - 백그라운드에서도 커스텀 사운드 재생을 위한 오디오 관리자
 *
 * 핵심 원리:
 * 1. 사용자 상호작용 시 AudioContext를 unlock
 * 2. 무음 오디오를 계속 재생하여 AudioContext를 활성 상태로 유지
 * 3. 알림 시 같은 AudioContext로 커스텀 사운드 재생
 *
 * 브라우저 제한 우회:
 * - 오디오가 이미 재생 중이면 백그라운드에서도 계속 재생됨
 * - 무음이라도 오디오 스트림을 유지하면 AudioContext가 suspended되지 않음
 */

class AudioManager {
  constructor() {
    this.audioContext = null;
    this.silentSource = null;
    this.audioBuffers = {};
    this.isUnlocked = false;
    this.isInitialized = false;

    // 사운드 파일 경로 매핑
    this.soundPaths = SOUND_PATHS;
  }

  /**
   * AudioContext 초기화 및 unlock
   * 반드시 사용자 상호작용(클릭, 터치 등) 이벤트 핸들러 내에서 호출해야 함
   */
  async initialize() {
    if (this.isInitialized) {
      // 이미 초기화됨 - resume만 시도
      if (this.audioContext?.state === 'suspended') {
        await this.audioContext.resume();
      }
      return true;
    }

    try {
      // AudioContext 생성
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)();

      // suspended 상태면 resume
      if (this.audioContext.state === 'suspended') {
        await this.audioContext.resume();
      }

      // 오디오 파일들을 버퍼로 미리 로드
      await this.preloadAllSounds();

      // 무음 루프 시작 (AudioContext 활성 유지)
      this.startSilentLoop();

      this.isUnlocked = true;
      this.isInitialized = true;

      console.log('🔊 [AudioManager] 초기화 완료 - AudioContext 상태:', this.audioContext.state);

      return true;
    } catch (error) {
      console.error('🔇 [AudioManager] 초기화 실패:', error);
      return false;
    }
  }

  /**
   * 모든 사운드 파일을 AudioBuffer로 미리 로드
   */
  async preloadAllSounds() {
    const loadPromises = Object.entries(this.soundPaths).map(async ([key, path]) => {
      try {
        const buffer = await this.loadAudioBuffer(path);
        this.audioBuffers[key] = buffer;
        console.log(`🔊 [AudioManager] ${key} 로드 완료`);
      } catch (error) {
        console.warn(`🔇 [AudioManager] ${key} 로드 실패:`, error);
      }
    });

    await Promise.all(loadPromises);
  }

  /**
   * 오디오 파일을 AudioBuffer로 로드
   */
  async loadAudioBuffer(path) {
    const response = await fetch(path);
    const arrayBuffer = await response.arrayBuffer();
    return await this.audioContext.decodeAudioData(arrayBuffer);
  }

  /**
   * 무음 루프 시작 - AudioContext를 활성 상태로 유지
   * 백그라운드 탭에서도 오디오 재생이 가능하도록 함
   */
  startSilentLoop() {
    if (!this.audioContext || this.silentSource) return;

    // 1초짜리 무음 버퍼 생성
    const sampleRate = this.audioContext.sampleRate;
    const buffer = this.audioContext.createBuffer(1, sampleRate, sampleRate);

    // 버퍼에 거의 무음 데이터 채우기 (완전 무음보다 약간의 노이즈가 더 안정적)
    const channelData = buffer.getChannelData(0);
    for (let i = 0; i < channelData.length; i++) {
      channelData[i] = (Math.random() - 0.5) * 0.0001; // 매우 작은 노이즈
    }

    this.silentSource = this.audioContext.createBufferSource();
    this.silentSource.buffer = buffer;
    this.silentSource.loop = true;

    // 볼륨을 거의 0으로 설정
    const gainNode = this.audioContext.createGain();
    gainNode.gain.value = 0.001; // 들리지 않는 수준

    this.silentSource.connect(gainNode);
    gainNode.connect(this.audioContext.destination);
    this.silentSource.start();

    console.log('🔊 [AudioManager] 무음 루프 시작 - AudioContext 활성 유지');
  }

  /**
   * 무음 루프 중지
   */
  stopSilentLoop() {
    if (this.silentSource) {
      this.silentSource.stop();
      this.silentSource.disconnect();
      this.silentSource = null;
      console.log('🔇 [AudioManager] 무음 루프 중지');
    }
  }

  /**
   * 사운드 재생
   * @param {string} soundKey - 사운드 키 (newMember, bankIn, bankOut, accountNo)
   * @returns {boolean} 재생 성공 여부
   */
  play(soundKey) {
    if (!this.isUnlocked || !this.audioContext) {
      console.warn('🔇 [AudioManager] AudioContext가 아직 unlock되지 않음');
      return false;
    }

    const buffer = this.audioBuffers[soundKey];
    if (!buffer) {
      console.warn(`🔇 [AudioManager] ${soundKey} 버퍼를 찾을 수 없음`);
      return false;
    }

    try {
      // AudioContext가 suspended면 resume 시도
      if (this.audioContext.state === 'suspended') {
        this.audioContext.resume();
      }

      // 새로운 소스 생성 및 재생
      const source = this.audioContext.createBufferSource();
      source.buffer = buffer;

      // 볼륨 조절용 GainNode
      const gainNode = this.audioContext.createGain();
      gainNode.gain.value = 0.8;

      source.connect(gainNode);
      gainNode.connect(this.audioContext.destination);
      source.start(0);

      console.log(`🔊 [AudioManager] ${soundKey} 재생`);
      return true;
    } catch (error) {
      console.error(`🔇 [AudioManager] ${soundKey} 재생 실패:`, error);
      return false;
    }
  }

  /**
   * 요청 유형에 따른 사운드 재생
   * @param {string} requestType - 요청 유형 ('register', 'deposit', 'withdraw', 'account')
   */
  playByRequestType(requestType) {
    const soundMap = {
      register: 'newMember',
      deposit: 'bankIn',
      withdraw: 'bankOut',
      account: 'accountNo',
    };

    const soundKey = soundMap[requestType];
    if (soundKey) {
      return this.play(soundKey);
    }
    return false;
  }

  /**
   * AudioContext 상태 확인
   */
  getStatus() {
    return {
      isInitialized: this.isInitialized,
      isUnlocked: this.isUnlocked,
      contextState: this.audioContext?.state || 'not created',
      loadedSounds: Object.keys(this.audioBuffers),
      silentLoopActive: !!this.silentSource,
    };
  }

  /**
   * 리소스 정리
   */
  dispose() {
    this.stopSilentLoop();
    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }
    this.audioBuffers = {};
    this.isUnlocked = false;
    this.isInitialized = false;
    console.log('🔇 [AudioManager] 리소스 정리 완료');
  }
}

// 싱글톤 인스턴스 export
const audioManager = new AudioManager();
export default audioManager;
